import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Text, useToast } from '@teals/ui-web';
import { usePatchOrgModelMutation } from '../../store/api/orgModelsApi';
import type { OrgModel } from '../../store/api/orgModelsApi';
import { modelEnableBlocked, mutationErrorMessage } from './modelsShared';

type PendingDisable = { model: OrgModel; agentNames: string[] };

/**
 * Turn models on/off for the org. Disabling a model that agents use as their
 * default asks for confirmation; enabling a payment-gated model goes to Payments.
 */
export function useModelToggle(orgId?: string) {
  const navigate = useNavigate();
  const { show } = useToast();
  const [patchOrgModel, { isLoading: patching }] = usePatchOrgModelMutation();
  const [pending, setPending] = useState<PendingDisable | null>(null);

  const apply = async (
    model: OrgModel,
    enabled: boolean,
    confirmInUse = false,
  ): Promise<boolean> => {
    if (!orgId) return false;
    try {
      await patchOrgModel({
        orgId,
        productId: model.productId,
        enabled,
        ...(confirmInUse ? { confirmInUse: true } : {}),
      }).unwrap();
      show({
        message: `${model.displayName} is ${enabled ? 'on' : 'off'}.`,
        type: 'success',
      });
      return true;
    } catch (error) {
      show({ message: mutationErrorMessage(error), type: 'error' });
      return false;
    }
  };

  const toggle = async (model: OrgModel, agentNames?: string[]): Promise<boolean> => {
    if (modelEnableBlocked(model)) {
      navigate('/payments');
      return false;
    }
    const inUse = agentNames ?? (model.usedAsDefaultBy || []).map((a) => a.name);
    if (model.enabled && inUse.length > 0) {
      setPending({ model, agentNames: inUse });
      return false;
    }
    return apply(model, !model.enabled);
  };

  const turnOn = async (model: OrgModel): Promise<boolean> => {
    if (modelEnableBlocked(model)) {
      navigate('/payments');
      return false;
    }
    return apply(model, true);
  };

  const confirmDisable = async () => {
    if (!pending) return;
    const ok = await apply(pending.model, false, true);
    if (ok) setPending(null);
  };

  const disableModal = (
    <Modal
      isOpen={pending != null}
      onClose={() => setPending(null)}
      title="Turn off model?"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <Text variant="body2">
          <strong>{pending?.model.displayName}</strong> is the default model for{' '}
          {pending?.agentNames.join(', ')}. Those agents can&apos;t run on it while it
          is off.
        </Text>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <Button variant="ghost" onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDisable} disabled={patching}>
            {patching ? 'Turning off…' : 'Turn off anyway'}
          </Button>
        </div>
      </div>
    </Modal>
  );

  return { toggle, turnOn, patching, disableModal };
}
